import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";

import {
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Card,
  CardBody,
  Badge,
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import { useParams, useNavigate } from "react-router-dom";
import { formatDateForDisplay } from "../../utils/dateUtils";
import { normalizeCurrentEmploymentForForm } from "utils/customerEmployment";
import UserInfoCards from "views/components/Users/UserInfoCards";
import { useGetCustomerDetail } from "api/customers/service";
import { getActiveColorStyle } from "utils/colorUtils";
import classNames from "classnames";
import { getTheme2FormFieldsCss } from "utils/theme2MultiSelect";
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from "utils/sidebarShellTheme";
import { useGetStates } from "api/states/service";
import CustomerDetailTab from "../components/Customers/CustomerDetailTab";
import FilesTab from "views/inventory/tabs/FilesTab";
import NotesTab from "views/inventory/tabs/NotesTab";
import { useTranslation } from "react-i18next";
import { FooterButton, HelpMenu, MfaGuideDrawer } from "components/Footer/components";
import { BUTTON_STYLES, DRAWER_STYLES, MFA_GUIDES } from "components/Footer/constants";
import { MdQrCode, MdDelete, MdBusinessCenter } from "react-icons/md";
import CustomerQrDrawer from "./components/CustomerQrDrawer";
import CustomerRefundDeposit from "./components/CustomerRefundDeposit";
import CustomerDepositHistory from "./components/CustomerDepositHistory";
import CustomerDeposit from "./components/CustomerDeposit";
import CustomerCreditBureau from "./components/CustomerCreditBureau";
import SubHeader from "components/Navbars/SubHeader";
import { useClearListStateOnLeave, LIST_STATE_FLOWS } from "utils/listStateSessionStorage";

const CUSTOMERS_LIST_STATE_KEY = "dealertiq-customers-list-state";

const initialForm = {
  email: "",
  alt_email: "",
  password: "",
  full_name: "",
  tenant_name: "",
  tenant_status: true,
  salutation: "",
  suffix: "",
  title: "",
  work_phone: "",
  home_phone: "",
  cell_phone: "",
  fax: "",
};

const CustomerDetail = ({
  activeColor = "blue",
  account,
  id: externalId,
  isLightTheme: isLightThemeProp,
  defaultTab,
  shellThemeVariant = "1",
  embedded = false,
}) => {
  const { t } = useTranslation();
  const { id: paramId } = useParams();
  useClearListStateOnLeave(CUSTOMERS_LIST_STATE_KEY, LIST_STATE_FLOWS.customers, {
    enabled: !embedded,
  });
  const id = externalId || paramId;
  const [activeTab, setActiveTab] = useState(defaultTab || "DETAIL");

  useEffect(() => {
    if (defaultTab) {
      setActiveTab(defaultTab);
    }
  }, [defaultTab]);

  const { data: customerDetail, isLoading, isFetching } = useGetCustomerDetail(id, activeTab);
  const [customerMainData, setCustomerMainData] = useState(null);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [tabLoading, setTabLoading] = useState(false);
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const { data: statesDataOriginal } = useGetStates();
  const statesData = statesDataOriginal?.data;
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState(initialForm);
  const tabs = [
    { id: "DETAIL", label: t('customerDetail.detail') },
    { id: "ADDRESS HISTORY", label: t('customerDetail.addressHistory') },
    { id: "EMPLOYMENT HISTORY", label: t('customerDetail.employmentHistory') },
    { id: "INCOME", label: t('customerDetail.income') },
    { id: "CREDIT BUREAU", label: t('customerDetail.creditBureau') },
    { id: "DEALS", label: t('customerDetail.deals') },
    { id: "ACCOUNTS", label: t('customerDetail.accounts') },
    { id: "NOTES", label: t('customerDetail.notes') },
    { id: "REFERENCES", label: t('customerDetail.references') },
    { id: "CREDIT REPORTING", label: t('customerDetail.creditReporting') },
    { id: "FILES", label: t('customerDetail.files') }
  ];
  const customerDetailTabRef = useRef();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const tabScrollRef = useRef(null);
  const [canScrollTabsRight, setCanScrollTabsRight] = useState(false);
  const isTheme2 = shellThemeVariant === "2";
  const theme2FieldsCss = useMemo(
    () =>
      isTheme2
        ? `${getTheme2FormFieldsCss({
            scopeSelector: ".customer-detail-page.customer-detail-page--theme2",
            fieldsSelector: ".customer-detail-fields",
          })}
          .customer-detail-page.customer-detail-page--theme2 .customer-detail-tabs .nav-link {
            font-size: ${THEME2_SIDEBAR_NAV_FONT_SIZE_VAR} !important;
          }
          .customer-detail-page.customer-detail-page--theme2 .customer-detail-fields table th,
          .customer-detail-page.customer-detail-page--theme2 .customer-detail-fields table td {
            font-size: ${THEME2_SIDEBAR_NAV_FONT_SIZE_VAR} !important;
          }
          .customer-detail-page.customer-detail-page--theme2 .customer-detail-fields .card-title,
          .customer-detail-page.customer-detail-page--theme2 .customer-detail-fields h5,
          .customer-detail-page.customer-detail-page--theme2 .customer-detail-fields h6 {
            font-size: calc(${THEME2_SIDEBAR_NAV_FONT_SIZE_VAR} + 2px) !important;
          }
          @media (max-width: 991.98px) {
            .sidebar.sidebar-shell-theme-2 ~ .main-panel > .content.customer-detail-page.customer-detail-page--theme2.theme2-admin-content-compact.theme2-customers-inset {
              padding-top: calc(74px + 45px) !important;
            }
          }
          @media (max-width: 575.98px) {
            .sidebar.sidebar-shell-theme-2 ~ .main-panel > .content.customer-detail-page.customer-detail-page--theme2.theme2-admin-content-compact.theme2-customers-inset {
              padding-top: calc(64px + 45px) !important;
            }
          }`
        : "",
    [isTheme2]
  );
  const customerActionDrawerCss = useMemo(
    () =>
      isTheme2
        ? getTheme2FormFieldsCss({
            scopeSelector:
              ".customer-action-drawer.customer-action-drawer--theme2",
            fieldsSelector: ".customer-action-drawer-form-fields",
          })
        : "",
    [isTheme2]
  );
  const useHorizontalMobileTabs = isMobile;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // No currency formatting needed for user info cards

  const [actionsClosed, setActionsClosed] = useState(false);
  const [hoverStates, setHoverStates] = useState({ button: null, text: null });
  const [drawerStates, setDrawerStates] = useState(() => {
    const initialStates = {
      howTo: false,
      chat: false,
      remove: false,
    };
    MFA_GUIDES.forEach((guide) => {
      initialStates[guide.drawerKey] = false;
    });
    return initialStates;
  });
  const [menuStates, setMenuStates] = useState({
    help: false,
    trainingHover: false,
  });
  const [customerQrDrawerVisible, setCustomerQrDrawerVisible] = useState(false);
  const [customerQrDrawerOpen, setCustomerQrDrawerOpen] = useState(false);
  const [customerActionDrawerVisible, setCustomerActionDrawerVisible] = useState(false);
  const [customerActionDrawerOpen, setCustomerActionDrawerOpen] = useState(false);
  const [customerActionDrawerActiveTab, setCustomerActionDrawerActiveTab] = useState("refundDeposit");
  const [customerActionDrawerFullSize, setCustomerActionDrawerFullSize] = useState(false);
  const customerActionDrawerWidthDragRef = useRef(null);
  const shouldShowCustomerQr = useMemo(
    () => customerMainData?.customer_qr?.qr_status === true,
    [customerMainData?.customer_qr?.qr_status]
  );

  useEffect(() => {
    if (customerDetail && !initialDataLoaded) {
      setInitialDataLoaded(true);
    }
  }, [customerDetail, initialDataLoaded]);

  useEffect(() => {
    if (!customerDetail) return;
    setCustomerMainData((prev) => {
      if (!prev) return customerDetail;
      return { ...prev, ...customerDetail };
    });
  }, [customerDetail]);

  useEffect(() => {
    if (initialDataLoaded && isFetching && !isLoading) {
      setTabLoading(true);
      const timer = setTimeout(() => {
        setTabLoading(false);
      }, 200);
      return () => clearTimeout(timer);
    }
    if (!isFetching) {
      setTabLoading(false);
    }
    return undefined;
  }, [isFetching, isLoading, initialDataLoaded]);

  const isTabContentLoading = tabLoading && isFetching;

  const handleDrawerToggle = useCallback((drawerName, isOpen) => {
    setDrawerStates((prev) => ({
      ...prev,
      [drawerName]: isOpen,
    }));
  }, []);

  const handleMenuToggle = useCallback((menuName, isOpen) => {
    setMenuStates((prev) => ({
      ...prev,
      [menuName]: isOpen,
    }));
  }, []);

  const handleHoverChange = useCallback((type, value) => {
    setHoverStates((prev) => ({
      ...prev,
      [type]: value,
    }));
  }, []);

  const handleQrClick = useCallback(() => {
    if (!shouldShowCustomerQr) {
      return;
    }
    setCustomerQrDrawerVisible(true);
    setTimeout(() => {
      setCustomerQrDrawerOpen(true);
    }, 0);
  }, [shouldShowCustomerQr]);

  const handleRemoveClick = useCallback(() => {
    handleDrawerToggle("remove", true);
  }, [handleDrawerToggle]);

  const handleRemoveCancel = useCallback(() => {
    handleDrawerToggle("remove", false);
  }, [handleDrawerToggle]);

  const handleRemoveConfirm = useCallback(() => {
    handleDrawerToggle("remove", false);
  }, [handleDrawerToggle]);

  const handleCustomerQrClose = useCallback(() => {
    setCustomerQrDrawerOpen(false);
  }, []);

  const handleActionDrawerClick = useCallback(() => {
    setCustomerActionDrawerVisible(true);
    setTimeout(() => {
      setCustomerActionDrawerOpen(true);
    }, 0);
  }, []);

  const handleActionDrawerClose = useCallback(() => {
    setCustomerActionDrawerOpen(false);
  }, []);

  const endCustomerActionDrawerWidthResizeInteraction = useCallback((clientX) => {
    const drag = customerActionDrawerWidthDragRef.current;
    if (!drag) return;
    customerActionDrawerWidthDragRef.current = null;
    const dx = clientX - drag.startX;
    if (Math.abs(dx) < 14) {
      setCustomerActionDrawerFullSize(!drag.startFull);
    } else if (dx < -42) {
      setCustomerActionDrawerFullSize(true);
    } else if (dx > 42) {
      setCustomerActionDrawerFullSize(false);
    }
  }, []);

  const onCustomerActionDrawerWidthHandlePointerDown = useCallback(
    (e) => {
      if (isMobile) return;
      e.preventDefault();
      e.stopPropagation();
      const el = e.currentTarget;
      if (typeof el.setPointerCapture === "function") {
        el.setPointerCapture(e.pointerId);
      }
      customerActionDrawerWidthDragRef.current = {
        startX: e.clientX,
        startFull: customerActionDrawerFullSize,
      };
      const onMove = () => {};
      const onUp = (ev) => {
        endCustomerActionDrawerWidthResizeInteraction(ev.clientX);
        try {
          if (typeof el.releasePointerCapture === "function") {
            el.releasePointerCapture(ev.pointerId);
          }
        } catch (_) {
          /* ignore */
        }
        el.removeEventListener("pointermove", onMove);
        el.removeEventListener("pointerup", onUp);
        el.removeEventListener("pointercancel", onUp);
      };
      el.addEventListener("pointermove", onMove, { passive: true });
      el.addEventListener("pointerup", onUp);
      el.addEventListener("pointercancel", onUp);
    },
    [
      isMobile,
      customerActionDrawerFullSize,
      endCustomerActionDrawerWidthResizeInteraction,
    ]
  );

  useEffect(() => {
    if (!customerQrDrawerOpen && customerQrDrawerVisible) {
      const timer = setTimeout(() => setCustomerQrDrawerVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [customerQrDrawerOpen, customerQrDrawerVisible]);

  useEffect(() => {
    if (!customerActionDrawerOpen && customerActionDrawerVisible) {
      const timer = setTimeout(() => setCustomerActionDrawerVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [customerActionDrawerOpen, customerActionDrawerVisible]);

  useEffect(() => {
    if (!shouldShowCustomerQr) {
      setCustomerQrDrawerOpen(false);
      setCustomerQrDrawerVisible(false);
    }
  }, [shouldShowCustomerQr]);


  const resolvedIsLightTheme = useMemo(() => {
    if (typeof isLightThemeProp === "boolean") {
      return isLightThemeProp;
    }
    if (typeof document === "undefined") {
      return false;
    }
    return document.body.classList.contains("white-content");
  }, [isLightThemeProp]);

  const customerQrDrawerStyles = useMemo(() => {
    const panelBackground = resolvedIsLightTheme ? "#ffffff" : "#1e1e2f";
    const textColor = resolvedIsLightTheme ? "#1e1e2f" : "#ffffff";
    const borderColor = resolvedIsLightTheme
      ? "rgba(0,0,0,0.1)"
      : "rgba(255,255,255,0.1)";

    return {
      overlay: {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0,0,0,0.35)",
        zIndex: 2000,
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "stretch",
        transition: "opacity 300ms ease",
      },
      panel: {
        position: "relative",
        width: "40vw",
        maxWidth: "960px",
        minWidth: "360px",
        height: "100vh",
        backgroundColor: panelBackground,
        color: textColor,
        boxShadow: "-8px 0 24px rgba(0,0,0,0.4)",
        transition: "transform 300ms ease",
        display: "flex",
        flexDirection: "column",
      },
      header: {
        padding: "12px 20px",
        borderBottom: `1px solid ${borderColor}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      },
      title: {
        margin: 0,
        fontSize: 16,
        fontWeight: 600,
        color: textColor,
      },
      body: {
        flex: 1,
        overflow: "auto",
        padding: "16px",
        backgroundColor: panelBackground,
        display: "flex",
        flexDirection: "column",
      },
      closeBtn: {
        ...activeColorStyle,
        color: "#ffffff",
        border: 0,
        padding: "6px 12px",
        fontWeight: 600,
        cursor: "pointer",
        borderRadius: 4,
      },
      shareButton: {
        marginTop: 24,
        width: "100%",
        padding: "16px 24px",
        borderRadius: 8,
        border: 0,
        fontWeight: 700,
        fontSize: 16,
        color: "#ffffff",
        background: activeColorStyle.background,
        backgroundImage: activeColorStyle.backgroundImage,
        cursor: "pointer",
      },
      shareFeedback: {
        marginTop: 8,
        fontSize: 13,
        color: activeColorStyle.borderColor,
        fontWeight: 500,
        textAlign: "center",
      },
    };
  }, [activeColorStyle, resolvedIsLightTheme]);

  const customerActionDrawerStyles = useMemo(() => {
    const panelBackground = resolvedIsLightTheme ? "#ffffff" : "#1e1e2f";
    const textColor = resolvedIsLightTheme ? "#1e1e2f" : "#ffffff";
    const borderColor = resolvedIsLightTheme
      ? "rgba(0,0,0,0.1)"
      : "rgba(255,255,255,0.1)";

    return {
      overlay: {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0,0,0,0.35)",
        zIndex: 2000,
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "stretch",
        opacity: customerActionDrawerOpen ? 1 : 0,
        pointerEvents: customerActionDrawerOpen ? "auto" : "none",
        transition: "opacity 300ms ease",
      },
      panel: {
        position: "relative",
        width: isMobile
          ? "100%"
          : customerActionDrawerFullSize
            ? "85vw"
            : "45vw",
        maxWidth: isMobile ? "100%" : "90vw",
        height: "100vh",
        backgroundColor: panelBackground,
        color: textColor,
        boxShadow: "-8px 0 24px rgba(0,0,0,0.4)",
        transform: customerActionDrawerOpen ? "translateX(0)" : "translateX(100%)",
        transition:
          "transform 300ms ease, width 220ms ease, max-width 220ms ease",
        display: "flex",
        flexDirection: "row",
        alignItems: "stretch",
      },
      drawerResizeHandle: {
        width: 18,
        flexShrink: 0,
        cursor: "ew-resize",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        touchAction: "none",
        userSelect: "none",
        WebkitUserSelect: "none",
        borderRight: resolvedIsLightTheme
          ? "1px solid rgba(0,0,0,0.08)"
          : "1px solid rgba(255,255,255,0.12)",
        backgroundColor: resolvedIsLightTheme
          ? "rgba(0,0,0,0.03)"
          : "rgba(255,255,255,0.04)",
      },
      mainColumn: {
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      },
      header: {
        padding: "0px 16px",
        flexShrink: 0,
        borderBottom: resolvedIsLightTheme
          ? "1px solid rgba(0,0,0,0.1)"
          : "1px solid rgba(255,255,255,0.1)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      },
      title: {
        margin: 0,
        fontSize: 16,
        fontWeight: 600,
        color: textColor,
      },
      body: {
        flex: 1,
        minHeight: 0,
        overflow: "auto",
        padding: "8px 16px 16px 16px",
        backgroundColor: panelBackground,
        display: "flex",
        flexDirection: "column",
      },
      closeBtn: {
        ...activeColorStyle,
        color: "white",
        border: 0,
        padding: "6px 12px",
        fontWeight: 600,
        cursor: "pointer",
      },
    };
  }, [
    activeColorStyle,
    resolvedIsLightTheme,
    customerActionDrawerOpen,
    customerActionDrawerFullSize,
    isMobile,
  ]);

  const getIconColor = useCallback(() => "#fff", []);

  const getButtonStyle = useCallback(
    (buttonId) => {
      const isHovered = hoverStates.button === buttonId;

      const baseStyle = {
        ...BUTTON_STYLES.base,
        backgroundColor: "#181b23",
        borderColor: "#fff",
        borderWidth: "1px",
        borderStyle: "solid",
        marginLeft: "4px",
        transform: "scale(1)",
        boxShadow: "none",
        filter: "none",
        zIndex: "auto",
      };

      const hoverStyle = isHovered
        ? {
          ...BUTTON_STYLES.hovered,
          backgroundColor: activeColorStyle.background,
          borderColor: activeColorStyle.borderColor,
          borderWidth: "3px",
          transform: "scale(1.25)",
          boxShadow:
            "0 8px 25px rgba(0, 0, 0, 0.5), 0 0 20px rgba(255, 255, 255, 0.3)",
          filter: "brightness(1.2) contrast(1.1)",
          zIndex: 1000,
        }
        : {};

      return {
        ...baseStyle,
        ...hoverStyle,
      };
    },
    [hoverStates.button, activeColorStyle]
  );


  const mapCurrentEmploymentForForm = useCallback((currentEmployment) => {
    const normalized = normalizeCurrentEmploymentForForm(currentEmployment);
    return normalized ? { current_employment: normalized } : {};
  }, []);

  const mapPreviousAddressForForm = useCallback((previousAddress) => {
    if (!previousAddress || typeof previousAddress !== "object") {
      return {};
    }

    if (previousAddress.prev_address_1 || previousAddress.prev_city) {
      return { previous_address: previousAddress };
    }

    return {
      previous_address: {
        prev_address_1: previousAddress.address_1 || "",
        prev_address_2: previousAddress.address_2 || "",
        prev_city: previousAddress.city || "",
        prev_zip_code: previousAddress.zip_code || previousAddress.zip || "",
        prev_county: previousAddress.county || "",
        prev_state: previousAddress.prev_state || previousAddress.state || "",
        prev_state_name:
          previousAddress.prev_state_name || previousAddress.state_name || "",
        prev_country: previousAddress.prev_country || previousAddress.country || "",
        prev_years_at: previousAddress.prev_years_at ?? previousAddress.years_at ?? "",
        prev_months_at: previousAddress.prev_months_at ?? previousAddress.months_at ?? "",
      },
    };
  }, []);

  useEffect(() => {
    if (activeTab !== "DETAIL" || !customerDetail) return;

    const previousEmployment = customerDetail.previous_employment || {};
    const previousAddressFields = mapPreviousAddressForForm(customerDetail.previous_address);
    const currentEmploymentFields = mapCurrentEmploymentForForm(
      customerDetail.current_employment
    );

    setFormData((prev) => ({
      ...prev,
      ...customerDetail,
      ...previousAddressFields,
      ...currentEmploymentFields,
      prev_employment_employer: previousEmployment.employer || "",
      prev_employment_supervisor_phone: previousEmployment.supervisor_phone || "",
      prev_employment_position: previousEmployment.position || "",
      prev_employment_type:
        previousEmployment.employment_type || previousEmployment.type || "",
      prev_employment_income: previousEmployment.income || "",
      prev_employment_pay_frequency: previousEmployment.pay_frequency || "",
      prev_employment_years_at: previousEmployment.years_at || "",
      prev_employment_months_at: previousEmployment.months_at || "",
      prev_employment_address_1: previousEmployment.address_1 || "",
      prev_employment_address_2: previousEmployment.address_2 || "",
      prev_employment_zip_code: previousEmployment.zip_code || "",
      prev_employment_city: previousEmployment.city || "",
      prev_employment_county: previousEmployment.county || "",
      prev_employment_state: previousEmployment.state || "",
      prev_employment_country: previousEmployment.country || "",
      prev_employment_email: previousEmployment.email || "",
    }));
  }, [customerDetail, activeTab, mapCurrentEmploymentForForm, mapPreviousAddressForForm]);

  const handleTabChange = useCallback((newTab) => {
    setActiveTab(newTab);
  }, []);

  const checkTabScroll = useCallback(() => {
    const el = tabScrollRef.current;
    if (!el) {
      setCanScrollTabsRight(false);
      return;
    }
    setCanScrollTabsRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  useEffect(() => {
    if (!useHorizontalMobileTabs) {
      setCanScrollTabsRight(false);
      return undefined;
    }
    const runCheck = () => {
      requestAnimationFrame(checkTabScroll);
    };
    runCheck();
    const scrollEl = tabScrollRef.current;
    scrollEl?.addEventListener("scroll", checkTabScroll);
    window.addEventListener("resize", runCheck);
    return () => {
      scrollEl?.removeEventListener("scroll", checkTabScroll);
      window.removeEventListener("resize", runCheck);
    };
  }, [checkTabScroll, tabs.length, id, useHorizontalMobileTabs]);

  const handleTabScrollRight = useCallback(() => {
    if (tabScrollRef.current && canScrollTabsRight) {
      tabScrollRef.current.scrollBy({ left: 200, behavior: "smooth" });
    }
  }, [canScrollTabsRight]);

  const handleChange = (name, value) => {
    setFormData(prev => {
      // Handle nested object updates (e.g., "current_address.address_1")
      if (name.includes('.')) {
        const keys = name.split('.');
        const newState = { ...prev };
        let current = newState;

        // Navigate to the nested object
        for (let i = 0; i < keys.length - 1; i++) {
          if (!current[keys[i]]) {
            current[keys[i]] = {};
          }
          current = current[keys[i]];
        }

        // Set the final value
        current[keys[keys.length - 1]] = value;
        return newState;
      }

      // Handle regular property updates
      return { ...prev, [name]: value };
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      let result = { success: false };
      if (activeTab === "DETAIL" && customerDetailTabRef.current) {
        result = await customerDetailTabRef.current.handleSave();
      }
    } catch (error) {
      console.log("CustomerDetail - error:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const pageClassName = useMemo(() => {
    const base = classNames("content customer-detail-page", {
      "customer-detail-page--theme2": isTheme2,
      "customer-detail-page--light": resolvedIsLightTheme,
      "customer-detail-page--dark": !resolvedIsLightTheme,
      "theme2-admin-content-compact": isTheme2,
      "theme2-customers-inset": isTheme2,
    });
    return base;
  }, [resolvedIsLightTheme, isTheme2]);

  const subHeaderTitle = useMemo(() => {
    const identifier = customerMainData?.customer_id || id;
    return identifier ? `Customer ${identifier}` : t("routes.customerDetail");
  }, [customerMainData?.customer_id, id, t]);

  return (
    <div
      className={pageClassName}
      style={{
        minHeight: "80vh",
        paddingTop: isMobile && !isTheme2 ? "7rem" : undefined,
      }}
    >
      {isTheme2 ? <style>{theme2FieldsCss}</style> : null}
      <div style={{ width: "100%" }}>
        {!embedded && (
          <div className="customer-detail-subheader-wrap">
            <SubHeader
              activeColor={activeColor}
              back={-1}
              backFallback="/customers"
              title={subHeaderTitle}
              status={customerMainData?.customer_credit}
              theme2ScrollGapFill={isTheme2}
              isLightTheme={resolvedIsLightTheme}
            />
          </div>
        )}
      <Row className="justify-content-center">
        <Col md="12">
          <div
            className={classNames("customer-detail-info-cards", {
              "users-info-cards-theme2": isTheme2,
              "users-info-cards-theme2--light": isTheme2 && resolvedIsLightTheme,
              "users-info-cards-theme2--dark": isTheme2 && !resolvedIsLightTheme,
            })}
          >
            <UserInfoCards
              data={customerMainData}
              getActiveColorStyle={() => activeColorStyle}
              isCustomer={true}
              isTheme2={isTheme2}
              isLightTheme={resolvedIsLightTheme}
              alwaysExpanded={isTheme2}
            />
          </div>
          <div
            className={
              useHorizontalMobileTabs
                ? "w-100 customer-detail-tabs detail-mobile-tabs"
                : "w-100 customer-detail-tabs"
            }
          >
            <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between">
              {useHorizontalMobileTabs ? (
                <div className="d-flex align-items-center flex-grow-1 min-width-0 w-100">
                  <div
                    ref={tabScrollRef}
                    className="flex-grow-1 channel-tabs-scroll"
                    style={{ minWidth: 0 }}
                  >
                    <Nav
                      tabs
                      className="border-0"
                      style={{
                        borderBottom: "none",
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "nowrap",
                        minWidth: "max-content",
                        width: "auto",
                        paddingLeft: 0,
                        paddingRight: 0,
                      }}
                    >
                      {tabs.map((tab) => (
                        <NavItem key={tab.id} style={{ flex: "0 0 auto" }}>
                          <NavLink
                            href="#"
                            className={activeTab === tab.id ? "active font-weight-bold" : "text-muted"}
                            style={{
                              background: "none",
                              border: "none",
                              position: "relative",
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              color: activeTab === tab.id ? activeColorStyle.borderColor : undefined,
                              fontWeight: activeTab === tab.id ? 700 : 500,
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              handleTabChange(tab.id);
                            }}
                          >
                            {tab.label}
                            {activeTab === tab.id && (
                              <div
                                style={{
                                  height: 3,
                                  background: activeColorStyle.borderColor,
                                  position: "absolute",
                                  left: 0,
                                  right: 0,
                                  bottom: -8,
                                }}
                              />
                            )}
                          </NavLink>
                        </NavItem>
                      ))}
                    </Nav>
                  </div>
                  {canScrollTabsRight && (
                    <button
                      type="button"
                      className="btn btn-link p-0 flex-shrink-0"
                      onClick={handleTabScrollRight}
                      style={{
                        color: activeColorStyle.borderColor,
                        lineHeight: 1,
                      }}
                      aria-label={t("common.scrollTabs")}
                    >
                      <i className="tim-icons icon-minimal-right" style={{ fontSize: 20 }} />
                    </button>
                  )}
                </div>
              ) : (
                <Nav tabs className="border-0" style={{ borderBottom: "none" }}>
                  {tabs.map(tab => (
                  <NavItem key={tab.id}>
                    <NavLink
                      href="#"
                      className={activeTab === tab.id ? "active font-weight-bold" : "text-muted"}
                      style={{
                        background: "none",
                        border: "none",
                        position: "relative",
                        cursor: 'pointer',
                        color: activeTab === tab.id ? activeColorStyle.borderColor : undefined,
                        fontWeight: activeTab === tab.id ? 700 : 500

                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        handleTabChange(tab.id);
                      }}
                    >
                      {tab.label}
                      {activeTab === tab.id && (
                        <div style={{ height: 3, background: activeColorStyle.borderColor, position: "absolute", left: 0, right: 0, bottom: -8 }} />
                      )}
                    </NavLink>
                  </NavItem>
                ))}
                </Nav>
              )}

              {/* Desktop View Save Button */}
              {account?.permissions?.customers_update && (activeTab === "DETAIL") && (
                <div className="d-none d-lg-flex align-items-center">
                  <button
                    className="btn btn-sm"
                    style={{ ...activeColorStyle, color: 'white', cursor: 'pointer', margin: 0 }}
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                        {t('customerDetail.saving')}
                      </>
                    ) : (
                      t('customerDetail.save')
                    )}
                  </button>
                </div>
              )}

              {/* Mobile View Save Button */}
              {account?.permissions?.customers_update && (activeTab === "DETAIL") && (
                <div className="d-flex d-lg-none flex-column w-100 mt-2">
                  <button
                    className="btn btn-sm w-100"
                    style={{ ...activeColorStyle, color: 'white', cursor: 'pointer', padding: '12px', margin: 0 }}
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                        {t('customerDetail.saving')}
                      </>
                    ) : (
                      t('customerDetail.save')
                    )}
                  </button>
                </div>
              )}
            </div>
            <TabContent activeTab={activeTab} className={classNames({ "customer-detail-fields": isTheme2 })}>
              <TabPane tabId="DETAIL">
                <CustomerDetailTab
                  formData={formData}
                  handleChange={handleChange}
                  id={id}
                  statesData={statesData}
                  activeColor={activeColor}
                  isTheme2={isTheme2}
                  ref={customerDetailTabRef}
                />
              </TabPane>
              <TabPane tabId="ADDRESS HISTORY">
                {isTabContentLoading ? (
                  <CustomerTabLoading
                    activeColorStyle={activeColorStyle}
                    label={t("common.updating")}
                  />
                ) : (
                <div className="card rounded-0">
                  <div className="card-body">
                    {(!customerDetail?.address_history || customerDetail.address_history.length === 0) ? (
                      <div>{t('customerDetail.noAddressHistoryFound')}</div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table">
                          <thead className="text-primary">
                            <tr>
                              <th>Address 1</th>
                              <th>Address 2</th>
                              <th>City</th>
                              <th>State</th>
                              <th>ZIP Code</th>
                            </tr>
                          </thead>
                          <tbody>
                            {customerDetail.address_history.map((addr, idx) => (
                              <tr key={idx}>
                                <td>{addr.address_1 || ''}</td>
                                <td>{addr.address_2 || ''}</td>
                                <td>{addr.city || ''}</td>
                                <td>{addr.state_name || addr.state || ''}</td>
                                <td>{addr.zip_code || addr.zip || ''}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
                )}
              </TabPane>

              <TabPane tabId="EMPLOYMENT HISTORY">
                {isTabContentLoading ? (
                  <CustomerTabLoading
                    activeColorStyle={activeColorStyle}
                    label={t("common.updating")}
                  />
                ) : (
                <div className="card rounded-0">
                  <div className="card-body">
                    {(!customerDetail?.employment_history || customerDetail.employment_history.length === 0) ? (
                      <div>{t('customerDetail.noEmploymentHistoryFound')}</div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table">
                          <thead className="text-primary">
                            <tr>
                              <th>Employer</th>
                              <th>Address 1</th>
                              <th>Address 2</th>
                              <th>City</th>
                              <th>State</th>
                              <th>ZIP Code</th>
                              <th>Position</th>
                              <th>Employment Date</th>
                              <th>Status</th>
                              <th>Status Date</th>
                              <th>Verified Method</th>
                            </tr>
                          </thead>
                          <tbody>
                            {customerDetail.employment_history.map((emp, idx) => (
                              <tr key={idx}>
                                <td>{emp.employer || emp.employment_employer || ''}</td>
                                <td>{emp.employment_address_1 || ''}</td>
                                <td>{emp.employment_address_2 || ''}</td>
                                <td>{emp.employment_city || ''}</td>
                                <td>{emp.employment_state || ''}</td>
                                <td>{emp.zip_code || emp.zip || ''}</td>
                                <td>{emp.employment_position || ''}</td>
                                <td>{emp.employment_date ? formatDateForDisplay(emp.employment_date) : ''}</td>
                                <td>{emp.status || ''}</td>
                                <td>{emp.status_date ? formatDateForDisplay(emp.status_date) : ''}</td>
                                <td>{emp.verified_method || ''}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
                )}
              </TabPane>
              <TabPane tabId="INCOME">
                {isTabContentLoading ? (
                  <CustomerTabLoading
                    activeColorStyle={activeColorStyle}
                    label={t("common.updating")}
                  />
                ) : (
                <div className="card rounded-0">
                  <div className="card-body">
                    {(() => {
                      const incomeData =
                        customerDetail?.income ??
                        customerDetail?.incomes ??
                        customerDetail?.income_history;
                      const hasIncome = Array.isArray(incomeData)
                        ? incomeData.length > 0
                        : incomeData &&
                          typeof incomeData === "object" &&
                          Object.keys(incomeData).length > 0;
                      if (!hasIncome) {
                        return <div>{t("customerDetail.noIncomeFound")}</div>;
                      }
                      return null;
                    })()}
                  </div>
                </div>
                )}
              </TabPane>
              <TabPane tabId="CREDIT BUREAU">
                <CustomerCreditBureau
                  creditBureau={customerDetail?.credit_bureau}
                  customerDetail={customerDetail}
                  mergedCustomerDetail={customerMainData}
                  isLoading={isTabContentLoading}
                  isTheme2={isTheme2}
                  isLightTheme={resolvedIsLightTheme}
                />
              </TabPane>
              <TabPane tabId="DEALS">
                <CustomerDeals deals={customerDetail?.deals} isLoading={isTabContentLoading} />
              </TabPane>
              <TabPane tabId="ACCOUNTS">
                <CustomerAccounts accounts={customerDetail?.calculuses} isLoading={isTabContentLoading} />
              </TabPane>
              <TabPane tabId="NOTES">
                <NotesTab module="customers" id={customerMainData?.id} permission={{
                  notes_add: account?.permissions?.customers_notes_add
                }} activeColor={activeColor} isTheme2={isTheme2} />
              </TabPane>
              <TabPane tabId="REFERENCES"></TabPane>
              <TabPane tabId="CREDIT REPORTING"></TabPane>
              <TabPane tabId="FILES">
                <FilesTab
                  module="customers"
                  type="file"
                  permission={{
                    add_files: account?.permissions?.customers_files_add,
                    delete_files: account?.permissions?.customers_files_add
                  }}
                  id={customerMainData?.id}
                  data={customerDetail?.files}
                  isLoading={isTabContentLoading}
                  activeColor={activeColor}
                />
              </TabPane>
            </TabContent>
          </div>
        </Col>
      </Row>
      </div>

      <div style={DRAWER_STYLES.floatingContainer}>
        {!actionsClosed && (
          <>
            <FooterButton
              id="action"
              icon={<MdBusinessCenter size={18} color={getIconColor()} />}
              onClick={handleActionDrawerClick}
              onHoverChange={(isHovered) =>
                handleHoverChange("button", isHovered ? "action" : null)
              }
              style={getButtonStyle("action")}
              iconColor={getIconColor()}
            />
            {shouldShowCustomerQr && (
              <FooterButton
                id="qr"
                icon={<MdQrCode size={18} color={getIconColor()} />}
                onClick={handleQrClick}
                onHoverChange={(isHovered) =>
                  handleHoverChange("button", isHovered ? "qr" : null)
                }
                style={getButtonStyle("qr")}
                iconColor={getIconColor()}
              />
            )}
            <FooterButton
              id="remove"
              className="customer-remove-btn"
              icon={<MdDelete size={18} color="#fff" />}
              onClick={handleRemoveClick}
              onHoverChange={(isHovered) =>
                handleHoverChange("button", isHovered ? "remove" : null)
              }
              style={{
                ...getButtonStyle("remove"),
                backgroundColor: activeColorStyle.borderColor,
                borderColor: activeColorStyle.borderColor,
              }}
              iconColor="#fff"
            />
          </>
        )}
        <FooterButton
          id="actions-toggle"
          icon={
            <i
              className={`tim-icons  ${actionsClosed ? "icon-minimal-up" : "icon-minimal-down"
                }`}
            />
          }
          onHoverChange={(isHovered) =>
            handleHoverChange("button", isHovered ? "actions-toggle" : null)
          }
          onClick={() => setActionsClosed((prev) => !prev)}
          style={getButtonStyle("actions-toggle")}
          iconColor={getIconColor()}
        />
      </div>

      <Modal
        isOpen={drawerStates.howTo}
        toggle={() => handleDrawerToggle("howTo", false)}
        size="lg"
        style={{
          marginTop: "80px",
        }}
      >
        <ModalHeader toggle={() => handleDrawerToggle("howTo", false)}>
          MFA How-To's
        </ModalHeader>
        <ModalBody>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {MFA_GUIDES.map((guide) => (
              <div
                key={guide.id}
                style={{
                  cursor: "pointer",
                  color: hoverStates.text === guide.id ? "#3b82f6" : undefined,
                  padding: "8px 0",
                }}
                onClick={() => handleDrawerToggle(guide.drawerKey, true)}
                onMouseEnter={() => handleHoverChange("text", guide.id)}
                onMouseLeave={() => handleHoverChange("text", null)}
              >
                {guide.title}
              </div>
            ))}
          </div>
        </ModalBody>
      </Modal>

      {MFA_GUIDES.map((guide) => (
        <MfaGuideDrawer
          key={guide.id}
          guide={guide}
          isOpen={drawerStates[guide.drawerKey]}
          onClose={() => handleDrawerToggle(guide.drawerKey, false)}
          zIndex={1000 + guide.zIndexOffset}
        />
      ))}

      <Modal
        isOpen={drawerStates.remove}
        toggle={handleRemoveCancel}
        centered={false}
        style={{ marginTop: "100px" }}
      >
        <div className="modal-header justify-content-center">
          <button
            aria-hidden={true}
            className="close"
            data-dismiss="modal"
            type="button"
            style={{ cursor: "pointer" }}
            onClick={handleRemoveCancel}
          >
            <i className="tim-icons icon-simple-remove" />
          </button>
          <h6 className="title title-up">{t("customers.deleteCustomer")}</h6>
        </div>
        <ModalBody className="text-center">
          <p>{t("customers.areYouSureDeleteCustomer")}</p>
          <p className="text-muted">{t("customers.cannotUndone")}</p>
        </ModalBody>
        <div className="modal-footer">
          <Button
            color="secondary"
            type="button"
            style={{ cursor: "pointer" }}
            onClick={handleRemoveCancel}
          >
            {t("customers.cancel")}
          </Button>
          <Button
            style={{
              background:
                "linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)",
              backgroundImage:
                "linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)",
              borderColor: "#fd5d93",
              color: "white",
              cursor: "pointer",
            }}
            color="danger"
            type="button"
            onClick={handleRemoveConfirm}
          >
            {t("customers.yesDelete")}
          </Button>
        </div>
      </Modal>

      <CustomerQrDrawer
        isVisible={customerQrDrawerVisible && shouldShowCustomerQr}
        isOpen={customerQrDrawerOpen}
        data={customerMainData?.customer_qr}
        styles={customerQrDrawerStyles}
        title={t("qr.title")}
        closeLabel={t("common.close")}
        onClose={handleCustomerQrClose}
      >
        {/* Drawer content can be implemented later */}
      </CustomerQrDrawer>

      {/* Customer Action Drawer */}
      {customerActionDrawerVisible && (
        <div
          style={customerActionDrawerStyles.overlay}
          onClick={handleActionDrawerClose}
        >
          <div
            className={
              isTheme2
                ? "customer-action-drawer customer-action-drawer--theme2"
                : "customer-action-drawer"
            }
            style={customerActionDrawerStyles.panel}
            onClick={(e) => e.stopPropagation()}
            onTransitionEnd={() => {
              if (!customerActionDrawerOpen) {
                setCustomerActionDrawerVisible(false);
              }
            }}
          >
            {customerActionDrawerCss ? (
              <style>{customerActionDrawerCss}</style>
            ) : null}
            {!isMobile && (
              <div
                style={customerActionDrawerStyles.drawerResizeHandle}
                onPointerDown={onCustomerActionDrawerWidthHandlePointerDown}
                title={t("accountsDrawer.drawerResizeTitle")}
                role="separator"
                aria-orientation="vertical"
                aria-label={t("accountsDrawer.drawerResizeAria")}
              >
                <div
                  style={{
                    width: 3,
                    height: 40,
                    backgroundColor: activeColorStyle.borderColor,
                    opacity: 0.55,
                  }}
                />
              </div>
            )}
            <div style={customerActionDrawerStyles.mainColumn}>
            <div style={customerActionDrawerStyles.header}>
              <h5 style={{ margin: 0 }}>
                <Nav tabs className="border-0" style={{ marginTop: "4px" }}>
                  {[
                    { key: "refundDeposit", translationKey: "customerDetail.tabRefundDeposit" },
                    { key: "depositHistory", translationKey: "customerDetail.tabDepositHistory" },
                    { key: "deposit", translationKey: "customerDetail.tabPostDeposit" },
                  ].map((tab) => (
                    <NavItem key={tab.key}>
                      <NavLink
                        href="#"
                        className={
                          customerActionDrawerActiveTab === tab.key
                            ? "active font-weight-bold"
                            : ""
                        }
                        style={{
                          background: "none",
                          border: "none",
                          fontSize: 12,
                          cursor: "pointer",
                          color:
                            customerActionDrawerActiveTab === tab.key
                              ? activeColorStyle.borderColor
                              : resolvedIsLightTheme
                                ? "#1e1e2f"
                                : "white",
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          setCustomerActionDrawerActiveTab(tab.key);
                        }}
                      >
                        {t(tab.translationKey)}
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>
              </h5>
              <button
                style={customerActionDrawerStyles.closeBtn}
                onClick={handleActionDrawerClose}
              >
                {t("common.close")}
              </button>
            </div>
            <div
              style={customerActionDrawerStyles.body}
              className={
                isTheme2 ? "customer-action-drawer-form-fields" : undefined
              }
            >
              {customerActionDrawerActiveTab === "refundDeposit" && (
                <CustomerRefundDeposit
                  activeColor={activeColor}
                  activeColorStyle={activeColorStyle}
                  isLightTheme={resolvedIsLightTheme}
                  isTheme2={isTheme2}
                  customerId={id}
                />
              )}
              {customerActionDrawerActiveTab === "depositHistory" && (
                <CustomerDepositHistory
                  activeColor={activeColor}
                  activeColorStyle={activeColorStyle}
                  isLightTheme={resolvedIsLightTheme}
                  isTheme2={isTheme2}
                  customerId={id}
                  customerDetail={customerMainData}
                />
              )}
              {customerActionDrawerActiveTab === "deposit" && (
                <CustomerDeposit
                  activeColor={activeColor}
                  activeColorStyle={activeColorStyle}
                  isLightTheme={resolvedIsLightTheme}
                  isTheme2={isTheme2}
                  customerId={id}
                />
              )}
            </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CustomerTabLoading = ({ activeColorStyle, label }) => (
  <div
    className="d-flex justify-content-center align-items-center"
    style={{ minHeight: "100px" }}
  >
    <div
      className="spinner-border spinner-border-sm"
      role="status"
      style={{ color: activeColorStyle.borderColor }}
    >
      <span className="sr-only">Loading...</span>
    </div>
    <span className="ml-2" style={{ color: activeColorStyle.borderColor, fontSize: "14px" }}>
      {label}
    </span>
  </div>
);

// CustomerDeals component
const CustomerDeals = ({ deals, isLoading = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();


  return (
    <Card className="mt-0">
      <CardBody>
        {isLoading ? (
          <div>{t('customerDetail.loadingCustomerDetails')}</div>
        ) : !deals || deals.length === 0 ? (
          <div>{t('customerDetail.noDealsFoundForCustomer')}</div>
        ) : (
          <Table >
            <thead className="text-primary">
              <tr>
                <th>{t('customerDetail.dealId')}</th>
                <th>{t('customerDetail.name')}</th>
                <th>{t('customerDetail.receivedDate')}</th>
                <th>{t('customerDetail.applicantDetails')}</th>
                <th>{t('customerDetail.salesBy')}</th>
                <th>{t('customerDetail.createdBy')}</th>
                <th>{t('customerDetail.dealStatus')}</th>
                <th>{t('customerDetail.flag')}</th>
                <th className="text-right"></th>
              </tr>
            </thead>
            <tbody>
              {deals.map((item) => (
                <tr key={item.id} onClick={() => navigate(`/deals/${item.documentId}`)}>
                  <td>{item.deal_id}</td>
                  <td>{item.name}</td>
                  <td>{formatDateForDisplay(item.received_date || item.created_date)}</td>
                  <td>{item.deal_type}</td>
                  <td>{item.sales_person_1}</td>
                  <td>{item.created_user?.user_detail?.full_name}</td>
                  <td><Badge color={item.deal_status === "Prospect" ? "success" : item.deal_status === "Working" ? "info" : item.deal_status === "Completed" ? "success" : "danger"}>{item.deal_status || ""}</Badge></td>
                  <td>{item.is_have_flags ? <Badge color="success">Yes</Badge> : <Badge color="danger">No</Badge>}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="9" className="border-0">
                  <div className="mt-0 text-right">
                    <small className="text-muted">{deals?.length} records in total</small>
                  </div>
                </td>
              </tr>
            </tfoot>
          </Table>
        )}
      </CardBody>
    </Card>
  );
};

const CustomerAccounts = ({ accounts, isLoading = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Card className="mt-0">
      <CardBody>
        {isLoading ? (
          <div>{t('customerDetail.loadingCustomerDetails')}</div>
        ) : !accounts || accounts.length === 0 ? (
          <div>{t('customerDetail.noAccountsFoundForCustomer')}</div>
        ) : (
          <Table >
            <thead className="text-primary">
              <tr>
                <th>{t('customerDetail.name')}</th>
                <th>{t('customerDetail.address')}</th>
                <th>{t('customerDetail.email')}</th>
                <th>{t('customerDetail.phone')}</th>
                <th>{t('customerDetail.status')}</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((item) => (
                <tr key={item.id} onClick={() => navigate(`/accounts/${item.booking_id}`)}>
                  <td>{item.customer?.full_name}</td>
                  <td>{item.customer?.current_address?.address_1}</td>
                  <td>{item.customer?.email}</td>
                  <td>{item.customer?.cell_phone}</td>
                  <td><Badge color={item.act_status === "active" ? "success" : item.act_status === "preliminary_work" ? "info" : item.act_status === "working" ? "success" : "danger"}>{item.act_status || ""}</Badge></td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="9" className="border-0">
                  <div className="mt-0 text-right">
                    <small className="text-muted">{accounts?.length} records in total</small>
                  </div>
                </td>
              </tr>
            </tfoot>
          </Table>
        )}
      </CardBody>
    </Card>
  );
};
export default CustomerDetail;
